import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRouteLoaderData } from "react-router-dom";
import type { AuthResponse } from "@/types/auth";
import { getSocket } from "@/lib/socket";
import { isSoundEnabled } from "@/lib/notification-sound";

function playNotificationSound() {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);
    osc.onended = () => ctx.close();
  } catch {
    // AudioContext unavailable (e.g. tests) — silently skip
  }
}

export function useNotificationsSocket() {
  const rootData = useRouteLoaderData("root") as AuthResponse | undefined;
  const userId = rootData?.user?.id;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!userId) return;

    const socket = getSocket();
    socket.connect();

    function onNewNotification() {
      if (isSoundEnabled()) playNotificationSound();
      void queryClient.invalidateQueries({ queryKey: ["notification-stats"] });
    }

    socket.on("notification:new", onNewNotification);

    return () => {
      socket.off("notification:new", onNewNotification);
      socket.disconnect();
    };
  }, [userId, queryClient]);
}
